import type {ReactNode} from 'react';
import {X} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '../drawer';
import {useScreenResize} from '../hooks';
import {DEVICE} from '../lib/device';
import {cn} from '../lib/cn';
import {getCustomContainer} from '../lib/utils';
import {isMobileModalModeAvailable} from '../lib/runtimeSettings';

export type LargeModalProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  header?: string | ReactNode;
  subHeader?: string | ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  screenReaderText?: string;
  withCloseButton?: boolean;
  closeOnDocumentClick?: boolean;
  closeOnEscape?: boolean;
  container?: HTMLElement;
  className?: string;
  headerClassName?: string;
  titleClassName?: string;
  drawerClassName?: string;
  drawerHeaderClassName?: string;
  drawerTitleClassName?: string;
  allowContentOverflow?: boolean;
  showDrawerHandle?: boolean;
  disableDrag?: boolean;
};

export function LargeModal({
  open,
  onOpenChange,
  children,
  header,
  subHeader,
  screenReaderText,
  className,
  headerClassName,
  titleClassName,
  drawerClassName,
  drawerHeaderClassName,
  drawerTitleClassName,
  footer,
  container,
  withCloseButton = true,
  closeOnDocumentClick = true,
  closeOnEscape = true,
  allowContentOverflow = false,
  showDrawerHandle = true,
  disableDrag = false,
}: LargeModalProps) {
  const portalContainer = container ?? getCustomContainer();
  const {isMatch: isMobile} = useScreenResize(DEVICE.tablet);
  const isMobileMode = isMobile && isMobileModalModeAvailable();

  if (isMobileMode) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          onClose={() => onOpenChange?.(false)}
          showHandle={showDrawerHandle}
          withCloseButton={withCloseButton}
          closeOnOverlayClick={closeOnDocumentClick}
          disableDrag={disableDrag}
          container={portalContainer || undefined}
          lockScroll={!portalContainer}
          onEscapeKeyDown={event => {
            if (!closeOnEscape) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          className={cn('bg-[var(--chekin-color-white)]', className, drawerClassName)}
        >
          <DrawerHeader
            className={cn(
              'flex-shrink-0 flex-row items-center justify-between border-b border-[var(--chekin-border-default)] bg-[var(--chekin-color-white)] px-6 pb-4 text-left',
              showDrawerHandle && 'sticky top-0 z-10',
              drawerHeaderClassName,
            )}
          >
            <div>
              <DrawerTitle
                className={cn(
                  'text-lg font-semibold text-[var(--chekin-color-brand-navy)]',
                  drawerTitleClassName,
                )}
              >
                {header}
              </DrawerTitle>
              {subHeader && <div>{subHeader}</div>}
            </div>
            <DrawerDescription className="sr-only">{screenReaderText}</DrawerDescription>
          </DrawerHeader>
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-4">
            {children}
          </div>
          {footer && <DrawerFooter className="p-4">{footer}</DrawerFooter>}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          'flex max-w-[calc(100%-2rem)] flex-col border-none p-0 sm:max-w-[calc(100%-2rem)] lg:max-w-4xl',
          !allowContentOverflow && 'max-h-[90vh]',
          className,
        )}
        container={portalContainer}
        lockScroll={!portalContainer}
        onEscapeKeyDown={event => {
          if (!closeOnEscape) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        onPointerDownOutside={event => {
          if (!closeOnDocumentClick) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
      >
        <DialogHeader
          className={cn(
            'flex-shrink-0 flex-row items-center justify-between rounded-t-[var(--dialog-content-radius)] border-b border-[var(--chekin-border-default)] bg-[var(--chekin-color-white)] p-6',
            headerClassName,
          )}
        >
          <div>
            <DialogTitle
              className={cn(
                'text-lg font-semibold text-[var(--chekin-color-brand-navy)]',
                titleClassName,
              )}
            >
              {header}
            </DialogTitle>
            {subHeader && <div>{subHeader}</div>}
          </div>
          {withCloseButton && (
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="rounded-full p-1 text-[var(--chekin-color-brand-navy)] hover:bg-[var(--chekin-color-surface-input-empty)]"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          <DialogDescription className="sr-only">{screenReaderText}</DialogDescription>
        </DialogHeader>
        <div
          className={cn(
            'min-h-0 flex-1 space-y-6 px-6 py-6',
            allowContentOverflow ? 'overflow-visible' : 'overflow-y-auto',
          )}
        >
          {children}
        </div>
        {footer && <DialogFooter className="p-4">{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
